// @flow

import React, { PureComponent } from 'react';
import { Tracking } from 'web-core/src/tracking';
import conf from 'client/conf';
import { TAX_LP_DOWNLOAD_CTA } from 'client/constants/amplitudeTracker';
import LinkButton from 'client/components/button/linkButton';
import SmsPhoneInput from 'client/components/form/smsPhoneInput';
import Stars from 'client/components/stars';
import TaxBlog from 'client/components/tax/blog';
import TaxFAQ from 'client/components/tax/faq';
import Testimonials from 'client/components/testimonials';
import TextSuccessMessage from 'client/components/textSuccessMessage';
import Translate from 'client/components/translate';
import TwoColSectionWithImage from 'client/components/grid/twoColSectionWithImage';
import '../styles/taxLanding.scss';

type Props = {
  brand: string,
  blogPosts: Array<Object>,
  fetchBlogItems: Function,
  heroLogoSrc: string | null,
  smsKey: string,
  smsSuccess: Object | null,
  source: string,
  contentfulData: Object,
};

class TaxLandingPage extends PureComponent<Props> {
  static defaultProps = {
    smsSuccess: null,
    brand: 'stride',
    source: 'Tax LP',
    heroLogoSrc: null,
  };

  UNSAFE_componentWillMount() {
    this.props.fetchBlogItems({ params: { section: 'taxes', omitBody: true } });
  }

  renderSMS() {
    const {
      smsKey,
      smsSuccess,
      source,
      contentfulData: { hero },
    } = this.props;

    return (
      <div>
        <div className="show-for-large">
          {smsSuccess ? (
            <TextSuccessMessage message={hero.smsSuccess} phoneNumber={smsSuccess.phone} />
          ) : (
            <SmsPhoneInput
              smsFailed={hero.smsFailed}
              smsCtaText={hero.smsCtaText}
              source={source}
              smsKey={smsKey}
              smsUrl="drive/sms" />
          )}
        </div>
        <LinkButton
          className="cta-button hide-for-large"
          href={conf.DRIVE_BRANCH_URL}
          onClick={() => {
            Tracking.track(TAX_LP_DOWNLOAD_CTA);
          }}>
          <span>{hero.getStrideTax}</span>
        </LinkButton>
      </div>
    );
  }

  render() {
    const {
      brand,
      contentfulData: { blog, faq, hero, sections, testimonials },
      heroLogoSrc,
    } = this.props;

    return (
      <div className="tax-landing">
        <TwoColSectionWithImage
          sectionClassName="hero white-bg"
          imageLocation="right"
          imageClassName="show-for-large"
          largeImage={<img alt="Expenses" style={{ width: 360 }} src={hero.heroImage} />}
          contentClassName="tax-header"
          content={
            <div className="text-left">
              <h1>
                <Translate fallback={hero.header} />
              </h1>
              {heroLogoSrc && (
                <div className="pad-top-2">
                  <img alt="Expenses" src={heroLogoSrc} />
                </div>
              )}
              <h4 className="pad-top">
                {brand === 'stride' ? (
                  <Translate fallback={hero.subheader} />
                ) : (
                  <Translate path={`taxes.landing.${brand}.hero.subheader`} />
                )}
              </h4>

              <div className="pad-top-2">{this.renderSMS()}</div>

              <h4 className="pad-top-2">
                <Translate fallback={hero.rating} />
              </h4>
              <Stars />
            </div>
          } />

        <section className="grey-6-bg">
          <div className="benefits section-col text-center">
            <h3 className="pad-x">
              <Translate fallback={sections.benefits.header} />
            </h3>
            <div className="row large-up-3 pad-top-2">
              <div className="column pad">
                <div>
                  <img alt="Car" src={sections.benefits.benefit1Image} />
                </div>
                <h4 className="pad-top grey-3-color">
                  <Translate fallback={sections.benefits.benefit1} />
                </h4>
              </div>
              <div className="column pad">
                <div>
                  <img alt="Receipt" src={sections.benefits.benefit2Image} />
                </div>
                <h4 className="pad-top grey-3-color">
                  <Translate fallback={sections.benefits.benefit2} />
                </h4>
              </div>
              <div className="column pad">
                <div>
                  <img alt="Report" src={sections.benefits.benefit3Image} />
                </div>
                <h4 className="pad-top grey-3-color">
                  {brand === 'stride' ? (
                    <Translate fallback={sections.benefits.benefit3} />
                  ) : (
                    <Translate path={`taxes.landing.${brand}.sections.benefits.benefit3`} />
                  )}
                </h4>
              </div>
            </div>
          </div>
        </section>

        <section className="white-bg text-center">
          <div className="tax-download section-col">
            <h3>
              <Translate fallback={sections.download.header} />
            </h3>
            <div className="pad-top-2 centered-sms">{this.renderSMS()}</div>
          </div>
        </section>

        <TwoColSectionWithImage
          forceImageTop
          sectionClassName="teal-bg-gradient"
          imageLocation="left"
          smallImage={
            <img alt="Mileage tracking" style={{ width: 300 }} src={sections.mileage.smallImage} />
          }
          largeImage={
            <img alt="Mileage tracking" style={{ width: 320 }} src={sections.mileage.largeImage} />
          }
          content={
            <div className="white-color">
              <h2>
                <Translate fallback={sections.mileage.header} />
              </h2>
              <h4 className="pad-top-2">
                <Translate fallback={sections.mileage.track} />
              </h4>
            </div>
          } />

        <TwoColSectionWithImage
          forceImageTop
          sectionClassName="grey-6-bg"
          imageLocation="right"
          smallImage={
            <img alt="Expenses" style={{ width: 320 }} src={sections.taxSavings.smallImage} />
          }
          largeImage={
            <img alt="Expenses" style={{ width: 360 }} src={sections.taxSavings.largeImage} />
          }
          content={
            <React.Fragment>
              <h2>
                <Translate fallback={sections.taxSavings.header} />
              </h2>
              <h4 className="pad-top-2">
                <Translate fallback={sections.taxSavings.receipts} />
              </h4>
              <h4 className="pad-top-2">
                <Translate fallback={sections.taxSavings.writeoffs} />
              </h4>
            </React.Fragment>
          } />

        <TwoColSectionWithImage
          forceImageTop
          sectionClassName="teal-bg-gradient"
          imageLocation="left"
          smallImage={
            <img alt="Tax Report" style={{ width: 320 }} src={sections.taxReport.smallImage} />
          }
          largeImage={
            <img alt="Tax Report" style={{ width: 360 }} src={sections.taxReport.largeImage} />
          }
          content={
            <div className="white-color">
              <h2>
                <Translate fallback={sections.taxReport.taxYear} />
              </h2>
              <h4 className="pad-top-2">
                {brand === 'stride' ? (
                  <Translate fallback={sections.taxReport.irsReport} />
                ) : (
                  <Translate path={`taxes.landing.${brand}.sections.taxReport.irsReport`} />
                )}
              </h4>
              <h4 className="pad-top-2">
                <Translate fallback={sections.taxReport.taxExperts} />
              </h4>
            </div>
          } />

        <section className="tax-testimonials">
          <Testimonials testimonials={testimonials} />

          <div className="text-center pad-top-3">
            <h3>
              <Translate fallback={sections.download.header} />
            </h3>
            <div className="centered-sms pad-top">{this.renderSMS()}</div>
          </div>
        </section>

        <TaxFAQ faqText={faq} />
        <TaxBlog blogPosts={this.props.blogPosts} blog={blog} />
      </div>
    );
  }
}

export default TaxLandingPage;
